import { getFieldNameWithPrefix } from '@monorepo/shared/componentsV2/fields/fieldUtils';
import { useMoveFocus } from '@monorepo/shared/hooks/useMoveFocus';
import React, { useCallback } from 'react';
import { FieldArray, useFieldArray } from 'react-final-form-arrays';
import { FieldConditionHelpers } from '../../hooks/useFieldConditionHelpers';
import { EditableFormTemplateField } from '../../types';
import { AddConditionallyShownFieldButton } from './AddConditionallyShownFieldButton';
import { ConditionallyShownFieldPicker } from './ConditionallyShownFieldPicker';
import { useGetAvailableAndUnavailableFields } from './useGetAvailableAndUnavailableFields';

interface ConditionallyShownFieldsProps {
  fieldsInGroup: EditableFormTemplateField[];
  fieldConditionHelpers: FieldConditionHelpers;
  rulePrefix: string;
  triggerFieldId: string;
}

export function ConditionallyShownFields({
  triggerFieldId,
  fieldConditionHelpers,
  fieldsInGroup,
  rulePrefix,
}: ConditionallyShownFieldsProps) {
  const formFieldName = getFieldNameWithPrefix(
    'conditionallyShownFields',
    rulePrefix,
  );

  const {
    fields: {
      value: conditionallyShownFields,
      remove: removeConditionallyShownField,
      push: addConditionallyShownField,
    },
  } = useFieldArray(formFieldName);

  const emptyFieldsCount = conditionallyShownFields.filter((f) => !f.id).length;

  const { unavailableFields, availableFields } =
    useGetAvailableAndUnavailableFields({
      fieldsInGroup,
      fieldConditionHelpers,
      triggerFieldId,
    });

  const { moveFocus, focusCatcherRef } = useMoveFocus();
  const onDelete = useCallback(
    (index: number, isLastField: boolean) => {
      const willDeleteButtonDisappear = conditionallyShownFields.length <= 2;
      const shouldMoveFocusAfterDelete =
        willDeleteButtonDisappear || isLastField;

      removeConditionallyShownField(index);
      if (shouldMoveFocusAfterDelete) {
        moveFocus();
      }
    },
    [conditionallyShownFields.length, removeConditionallyShownField, moveFocus],
  );

  return (
    <>
      <FieldArray name={formFieldName}>
        {({ fields }) => (
          <>
            {fields.map((name, index) => {
              const isFirstField = index === 0;
              const isLastField = index === fields.value.length - 1;

              return (
                <ConditionallyShownFieldPicker
                  key={name}
                  formFieldName={name}
                  fieldConditionHelpers={fieldConditionHelpers}
                  isFirstField={isFirstField}
                  removeConditionallyShownField={() =>
                    onDelete(index, isLastField)
                  }
                  unavailableFields={unavailableFields}
                  availableFields={availableFields}
                  isDeletable={conditionallyShownFields.length > 1}
                />
              );
            })}
          </>
        )}
      </FieldArray>
      <AddConditionallyShownFieldButton
        focusCatcherRef={focusCatcherRef}
        addConditionallyShownField={addConditionallyShownField}
        canAddConditionallyShownField={
          availableFields.length > 0 &&
          emptyFieldsCount < availableFields.length
        }
      />
    </>
  );
}
